import React, { useState } from 'react'
import { NewSideBarMenu, SIDEBAR_GROUPS, UserSidebarData } from './data'
import { useRouter } from 'next/router'
import { useStore } from '@/src/types/store'

export interface Tab {
  id: string | number
  read?: boolean | undefined
  write?: boolean | undefined
  name: string
  title: string
  href: string
  icon?: React.ReactNode
  titleClassName?: string
  children?: Tab[]
  activePrefixes?: string[]
}

// Event analyses aggregated by the Capital Events landing page; the item is
// visible when the user can read any of them AND the matching tenant
// feature flag is on (checked in handlePermission).
const CAPITAL_EVENT_PERMISSION_NAMES = ['Placements', 'Post IPO']

export const NewSideBar = () => {
  const { permissions, profile, homeTheme } = useStore()
  const router = useRouter()
  const CurrTab = router?.pathname
  const isUser = profile?.user_type === 'investor' || profile?.user_type === 'user' ? true : false
  const baseMenu = isUser ? UserSidebarData(CurrTab) : NewSideBarMenu(CurrTab)
  // When daily reports are hidden for the tenant, drop the "Home" (daily report) item.
  const NavbarData = homeTheme?.hide_daily_report
    ? baseMenu.filter((tab: any) => tab?.id !== 'dashboard')
    : baseMenu
  const [openSubmenu, setOpenSubmenu] = useState<string | number | null>(null)

  const handlePermission = (tab: Tab) => {
    if (tab?.id === 'capital_events') {
      return CAPITAL_EVENT_PERMISSION_NAMES.some(name => {
        const perm = permissions?.find(item => item?.name === name)
        if (perm?.read !== true) return false
        if (name === 'Placements') return !!homeTheme?.enable_placements
        if (name === 'Post IPO') return !!homeTheme?.enable_post_ipo
        return true
      })
    }
    const findTab = permissions?.find(item => item?.name === tab?.name)
    return findTab?.read === true
  }

  // A divider renders between consecutive visible items whose intent
  // cluster differs - whitespace organisation, not another nav control.
  // Computed on visible items only so permission gating never strands one.
  const needsDivider = (prevTab: any, tab: any) => {
    const a = SIDEBAR_GROUPS[prevTab?.id]
    const b = SIDEBAR_GROUPS[tab?.id]
    return a !== undefined && b !== undefined && a !== b
  }

  const isTabActive = (tab: any) => {
    if (tab?.activePrefixes?.length) {
      return tab.activePrefixes.some(
        (p: string) => CurrTab === p || CurrTab.startsWith(p + '/')
      )
    }
    const href = tab?.href
    if (!href || href === '/') return CurrTab === href
    if (CurrTab === href || CurrTab.startsWith(href + '/')) return true
    if (tab.children?.length) {
      return tab.children.some((child: any) => {
        const ch = child?.href
        if (!ch || ch === '/') return CurrTab === ch
        return CurrTab === ch || CurrTab.startsWith(ch + '/')
      })
    }
    return false
  }

  return (
    <>
      <div
        data-app-sidebar
        className="hidden h-full sm:flex flex-col justify-between pt-2 px-1 lg:hidden"
      >
        <div className="text-white bg-primary-main rounded-[20px] py-4">
          {(isUser ? permissions : true) && (
            <div className="flex flex-col gap-1 ">
              {NavbarData.filter((tab: any) =>
                isUser ? true : handlePermission(tab)
              ).map((tab: any, index: number, visible: any[]) => (
                    <React.Fragment key={tab.id || index}>
                    {index > 0 && needsDivider(visible[index - 1], tab) && (
                      <div className="mx-3 my-0.5 h-px bg-white/20" />
                    )}
                    <div
                      className={`flex items-center  cursor-pointer gap-3.5 w-full py-2 px-4 ${
                        isTabActive(tab)
                          ? ' bg-secondary-blue text-white'
                          : 'hover:bg-secondary-blue'
                      }`}
                      onClick={() => {
                        if (tab?.children) {
                          setOpenSubmenu(openSubmenu === tab.id ? null : tab.id)
                          router.push({ pathname: `${tab?.href}` })
                        } else {
                          setOpenSubmenu(null)
                          router.push({ pathname: `${tab?.href}` })
                        }
                      }}
                    >
                      <div
                        style={{
                          fontWeight: isTabActive(tab) ? 'bold' : 'normal'
                        }}
                      >
                        {tab?.icon}
                      </div>
                    </div>
                    {tab?.children && (isTabActive(tab) || openSubmenu === tab.id) && (
                      <div className="flex flex-col gap-[2px]">
                        {tab.children.map((child: any, childIndex: number) => (
                          <div
                            key={childIndex}
                            className={`flex items-center cursor-pointer py-2 pl-12 pr-4 text-xs text-white ${
                              CurrTab === child.href || CurrTab.startsWith(child.href + '/')
                                ? 'bg-secondary-blue'
                                : 'hover:bg-secondary-blue'
                            }`}
                            onClick={() => router.push({ pathname: child.href })}
                          >
                            {child.title}
                          </div>
                        ))}
                      </div>
                    )}
                    </React.Fragment>
                  )
              )}
            </div>
          )}
        </div>
      </div>
      <div
        data-app-sidebar
        className="hidden h-full w-[246px] lg:flex flex-col justify-between pt-2 px-1 "
      >
        <div className="text-white w-[230px] bg-primary-main rounded-[20px] py-4 overflow-y-auto">
          {(isUser ? permissions : true) && (
            <div className="flex flex-col gap-1 ">
              {(() => {
                const visibleTabs = NavbarData.filter((tab: any) =>
                  isUser ? true : handlePermission(tab)
                )

                const elements: React.ReactNode[] = []

                visibleTabs.forEach((tab: any, i: number) => {
                  const active = isTabActive(tab)

                  if (i > 0 && needsDivider(visibleTabs[i - 1], tab)) {
                    elements.push(
                      <div
                        key={`divider-${i}`}
                        className="mx-4 my-0.5 h-px bg-white/20"
                      />
                    )
                  }

                  elements.push(
                    <div
                      key={`tab-${i}`}
                      className={`flex items-center cursor-pointer gap-3.5 w-full py-2 px-4 ${
                        active
                          ? 'bg-secondary-blue text-white'
                          : 'hover:bg-secondary-blue'
                      }`}
                      onClick={() =>
                        router.push({
                          pathname: `${tab?.href}`
                        })
                      }
                    >
                      <div
                        style={{
                          fontWeight: active ? 'bold' : 'normal'
                        }}
                      >
                        {tab?.icon}
                      </div>
                      <span className={`w-44 text-xs font-normal ${tab.titleClassName}`}>
                        {tab.title}
                      </span>
                    </div>
                  )

                  if (tab.children && isTabActive(tab)) {
                    elements.push(
                      <div
                        key={`tab-${i}-children`}
                        className="relative -mt-2"
                      >
                        <div className="pointer-events-none absolute top-0 bottom-0 left-[28px] w-px bg-white/20" />
                        {tab.children.map((child: any, j: number) => {
                          const childActive =
                            CurrTab === child.href ||
                            CurrTab.startsWith(child.href + '/')
                          return (
                            <div
                              key={`tab-${i}-child-${j}`}
                              className={`relative flex items-center cursor-pointer w-full py-2 pl-[54px] pr-4 ${
                                childActive
                                  ? 'bg-secondary-blue text-white'
                                  : 'hover:bg-secondary-blue'
                              }`}
                              onClick={() =>
                                router.push({
                                  pathname: `${child?.href}`
                                })
                              }
                            >
                              <span className={`text-xs ${child.titleClassName}`}>
                                {child.title}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    )
                  }
                })

                return elements
              })()}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default NewSideBar
